import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { NavBar } from '../components/NavBar';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { 
  Apple,
  Droplets,
  Moon,
  UtensilsCrossed,
  Beef,
  ChevronRight,
  Sparkles,
  ArrowRight,
  Lock,
  Crown
} from 'lucide-react';
import { motion } from 'framer-motion';

const categoryIcons = {
  protein: Beef,
  hydration: Droplets,
  recovery: Moon,
  meal_prep: UtensilsCrossed
};

const categoryColors = {
  protein: 'bg-red-500/20 text-red-400 border-red-500/30',
  hydration: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  recovery: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  meal_prep: 'bg-green-500/20 text-green-400 border-green-500/30'
};

export const NutritionPage = () => {
  const { api, user } = useAuth();
  const [tips, setTips] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  const isAdmin = !!user?.is_admin;
  const isPremium = !!user?.is_premium;
  const premiumLocked = !isPremium && !isAdmin;

  useEffect(() => {
    const fetchTips = async () => {
      try {
        const res = await api().get('/nutrition');
        setTips(res.data);
      } catch {
        // silent
      }
      setLoading(false);
    };
    fetchTips();
  }, [api]);

  const categories = ['all', 'protein', 'hydration', 'recovery', 'meal_prep'];
  
  const filteredTips = selectedCategory === 'all' 
    ? tips 
    : tips.filter(t => t.category === selectedCategory);

  return (
    <div className="min-h-screen bg-black" data-testid="nutrition-page">
      <NavBar title="Nutrition" />

      <main className="container mx-auto px-6 py-8">
        {/* Hero */}
        <div className="relative mb-10 overflow-hidden">
          <div className="absolute inset-0">
            <img 
              src="https://images.pexels.com/photos/1640771/pexels-photo-1640771.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Nutrition"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 to-black/70" />
          </div>
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative z-10 py-16 text-center md:text-left"
          >
            <div className="flex items-center gap-2 mb-4 justify-center md:justify-start">
              <Apple className="h-6 w-6 text-primary" />
              <span className="text-primary font-heading text-xl tracking-widest uppercase">Fuel Your Body</span>
            </div>
            <h2 className="font-heading text-5xl md:text-6xl text-white uppercase tracking-wider mb-4">
              Nutrition <span className="text-primary">Tips</span>
            </h2>
            <p className="text-white/70 text-lg max-w-xl mx-auto md:mx-0">
              Your fitness journey is only half the battle. Proper nutrition accelerates your results and keeps you energized.
            </p>
          </motion.div>
        </div>

        {/* AI Plan Purchase CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className={`mb-10 bg-gradient-to-br ${premiumLocked ? 'from-yellow-500/15 via-card to-card border-yellow-500/40' : 'from-green-500/15 via-card to-card border-green-500/30'} border rounded-2xl p-6 md:p-8`}
          data-testid="nutrition-plan-cta"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                {premiumLocked ? (
                  <>
                    <Lock className="h-5 w-5 text-yellow-400" />
                    <span className="text-yellow-400 font-heading text-sm tracking-widest uppercase">Premium Members Only</span>
                  </>
                ) : (
                  <>
                    <Sparkles className="h-5 w-5 text-green-400" />
                    <span className="text-green-400 font-heading text-sm tracking-widest uppercase">AI Custom Plans</span>
                  </>
                )}
              </div>
              <h3 className="font-heading text-2xl md:text-3xl text-white uppercase tracking-wider mb-2">
                {premiumLocked ? (
                  <>Unlock your <span className="text-yellow-400">personalized meal plan</span></>
                ) : (
                  <>Get your <span className="text-primary">personalized meal plan</span></>
                )}
              </h3>
              <p className="text-muted-foreground text-sm max-w-xl">
                {premiumLocked
                  ? 'Nutrition plans are exclusive to premium subscribers. Unlock unlimited AI meal plans, grocery lists, and meal swaps.'
                  : 'AI-crafted meal plans built around your goals, macros, and dietary preferences. Pick 4, 8, or 12 weeks — from $150.'}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto flex-shrink-0">
              {premiumLocked ? (
                <Link to="/subscription">
                  <Button className="w-full sm:w-auto bg-yellow-500 text-black font-heading tracking-wider uppercase hover:bg-yellow-400" data-testid="unlock-premium-btn">
                    <Crown className="h-4 w-4 mr-2" />
                    Unlock With Premium
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/nutrition-plans/my">
                    <Button variant="outline" className="w-full sm:w-auto border-white/20 text-white hover:bg-white/10" data-testid="my-plans-link">
                      My Plans
                    </Button>
                  </Link>
                  <Link to="/nutrition-plans">
                    <Button className="w-full sm:w-auto bg-primary text-black font-heading tracking-wider uppercase hover:bg-primary/90" data-testid="buy-plan-link">
                      <ArrowRight className="h-4 w-4 mr-2" />
                      Buy a Plan
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </motion.div>

        {/* Category Filter */}
        <div className="mb-10 max-w-xs">
          <label className="block text-muted-foreground text-xs uppercase tracking-[0.2em] font-heading mb-2">
            Category
          </label>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger
              data-testid="category-select"
              className="h-12 bg-card border border-primary/30 text-white font-heading uppercase tracking-wider text-sm px-4 hover:border-primary hover:bg-card/80 focus:ring-2 focus:ring-primary/60 focus:border-primary transition-colors data-[state=open]:border-primary data-[state=open]:ring-2 data-[state=open]:ring-primary/60"
            >
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent
              className="bg-zinc-950 border border-primary/40 text-white shadow-2xl shadow-primary/20"
              position="popper"
            >
              {categories.map((cat) => {
                const Icon = cat === 'all' ? Apple : categoryIcons[cat];
                const label = cat === 'all' ? 'All Categories' : cat === 'meal_prep' ? 'Meal Prep' : cat;
                return (
                  <SelectItem
                    key={cat}
                    value={cat}
                    data-testid={`category-${cat}`}
                    className="font-heading uppercase tracking-wider text-sm cursor-pointer text-white hover:!bg-primary/20 focus:!bg-primary/20 focus:!text-primary data-[state=checked]:!text-primary data-[state=checked]:!bg-primary/10 [&>span:first-child]:text-primary"
                  >
                    <span className="flex items-center gap-2">
                      <Icon className="h-4 w-4" />
                      {label}
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>

        {/* Tips Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTips.map((tip, i) => {
            const Icon = categoryIcons[tip.category] || Apple;
            return (
              <motion.div
                key={tip.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="bg-card border border-border rounded-2xl overflow-hidden group card-hover"
              >
                <div className="relative h-48">
                  <img 
                    src={tip.image_url}
                    alt={tip.title}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />
                  <div className="absolute bottom-4 left-4">
                    <Badge className={`${categoryColors[tip.category]} border`}>
                      <Icon className="h-3 w-3 mr-1" />
                      {tip.category === 'meal_prep' ? 'Meal Prep' : tip.category}
                    </Badge>
                  </div>
                </div>
                
                <div className="p-6">
                  <h3 className="font-heading text-2xl text-white uppercase tracking-wider mb-3">{tip.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{tip.content}</p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {filteredTips.length === 0 && !loading && (
          <div className="text-center py-20">
            <Apple className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground text-lg">No nutrition tips found for this category</p>
          </div>
        )}

        {/* Quick Tips Section */}
        <div className="mt-16">
          <h3 className="font-heading text-3xl text-white uppercase tracking-wider mb-8">Quick Guidelines</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-card border border-border rounded-2xl p-6">
              <Beef className="h-10 w-10 text-red-400 mb-4" />
              <h4 className="font-heading text-xl text-white uppercase tracking-wider mb-2">Protein Goal</h4>
              <p className="text-muted-foreground">0.8-1g per pound of body weight daily for muscle growth</p>
            </div>
            <div className="bg-card border border-border rounded-2xl p-6">
              <Droplets className="h-10 w-10 text-blue-400 mb-4" />
              <h4 className="font-heading text-xl text-white uppercase tracking-wider mb-2">Hydration</h4>
              <p className="text-muted-foreground">Half your body weight in ounces of water daily</p>
            </div>
            <div className="bg-card border border-border rounded-2xl p-6">
              <UtensilsCrossed className="h-10 w-10 text-green-400 mb-4" />
              <h4 className="font-heading text-xl text-white uppercase tracking-wider mb-2">Meal Timing</h4>
              <p className="text-muted-foreground">Eat protein within 2 hours post-workout for recovery</p>
            </div>
            <div className="bg-card border border-border rounded-2xl p-6">
              <Moon className="h-10 w-10 text-purple-400 mb-4" />
              <h4 className="font-heading text-xl text-white uppercase tracking-wider mb-2">Recovery</h4>
              <p className="text-muted-foreground">7-9 hours of quality sleep for optimal results</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};
